import { useEffect, useRef, useState } from "react";
import { Alert, Modal, Stack, Text } from "@mantine/core";

interface DetectorLike {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}

/** Camera barcode scanning: native BarcodeDetector when available, ZXing otherwise. */
export function BarcodeScanner({ opened, onClose, onDetected }: { opened: boolean; onClose: () => void; onDetected: (code: string) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let zxingControls: { stop: () => void } | null = null;
    setError(null);

    const done = (code: string) => {
      if (stopped) return;
      stopped = true;
      onDetected(code);
      onClose();
    };

    (async () => {
      try {
        const Native = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => DetectorLike }).BarcodeDetector;
        if (Native) {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          const video = videoRef.current!;
          video.srcObject = stream;
          await video.play();
          const detector = new Native({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] });
          const tick = async () => {
            if (stopped) return;
            const found = await detector.detect(video).catch(() => []);
            if (found[0]?.rawValue) return done(found[0].rawValue);
            requestAnimationFrame(tick);
          };
          void tick();
        } else {
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          const reader = new BrowserMultiFormatReader();
          zxingControls = await reader.decodeFromVideoDevice(undefined, videoRef.current!, (result) => {
            if (result) done(result.getText());
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Camera unavailable");
      }
    })();

    return () => {
      stopped = true;
      zxingControls?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [opened, onClose, onDetected]);

  return (
    <Modal opened={opened} onClose={onClose} title="Scan a barcode" size="lg" centered>
      <Stack>
        {error ? (
          <Alert color="orange">Couldn't start the camera: {error}. You can type the barcode instead.</Alert>
        ) : (
          <Text c="dimmed" fz="sm">
            Point the camera at a UPC / EAN barcode.
          </Text>
        )}
        <video ref={videoRef} muted playsInline style={{ width: "100%", borderRadius: 12, background: "#000", minHeight: 240 }} />
      </Stack>
    </Modal>
  );
}
